import { FormEvent, useCallback, useMemo, useState } from "react";
import {
  Flex,
  FlexItem,
  Form,
  Gallery,
  GalleryItem,
  Stack,
  StackItem,
  Content,
  Title,
  TitleSizes,
} from "@patternfly/react-core";
import LoadingEmptyState from "../components/LoadingEmptyState";
import { TestingLocationCard } from "../components/TestingLocationCard";
import { useGetPublicTestingLocationsQuery } from "../../features/api/getPublicTestingLocationsApi";
import ReadyToTestLink from "../components/links/ReadyToTestLink";
import NeedToTrainFirstLink from "../components/links/NeedToTrainFirstLink";
import SelectConfiguredCountryFormGroup from "../components/forms/formgroups/SelectConfiguredCountryFormGroup";

export default function TestingLocations() {
  const [alpha2, setAlpha2] = useState<string>("");
  const { isFetching, data: testingLocations = [] } =
    useGetPublicTestingLocationsQuery(undefined);
  const availableCountries: Set<string> = useMemo(
    () => new Set(testingLocations.map((location) => location.iso31661.alpha2)),
    [testingLocations],
  );
  const locations = useMemo(
    () =>
      [...testingLocations].sort((a, b) => a.country.localeCompare(b.country)),
    [testingLocations],
  );
  const countryValidator = useCallback(
    (alpha2: string) => {
      return availableCountries.has(alpha2);
    },
    [availableCountries],
  );
  const handleCountryChange = useCallback(
    (_: FormEvent<HTMLSelectElement>, alpha2: string) => {
      setAlpha2(alpha2);
    },
    [],
  );

  if (isFetching) {
    return <LoadingEmptyState />;
  }

  const instructions = `Red Hat's certification exams are available at ${testingLocations.length.toString()} locations worldwide. Select a country below to find the location nearest you.`;

  return (
    <Stack hasGutter style={{ height: "100%" }}>
      <StackItem>
        <Title headingLevel="h1" size={TitleSizes["2xl"]}>
          Red Hat Exam Locations
        </Title>
        <Content component="small">{instructions}</Content>
        <Form>
          <SelectConfiguredCountryFormGroup
            id="select-a-country"
            value={alpha2}
            onChange={handleCountryChange}
            validator={countryValidator}
          />
        </Form>
      </StackItem>
      <StackItem>
        <Gallery hasGutter>
          {locations
            .filter((location) => alpha2 === location.iso31661.alpha2)
            .map((location) => {
              const {
                code,
                city,
                state,
                address1,
                address2,
                zipCode,
                country,
                contactNumber,
              } = location;
              const address: string = [
                address1,
                address2,
                `${city} ${state}`,
                zipCode,
                country,
              ]
                .filter((addressComponent) => "" !== addressComponent)
                .join(", ");
              return (
                <GalleryItem key={code}>
                  <TestingLocationCard
                    name={code}
                    address={address}
                    contact={contactNumber}
                  />
                </GalleryItem>
              );
            })}
        </Gallery>
      </StackItem>
      <StackItem>
        <Flex>
          <FlexItem align={{ default: "alignRight" }}>
            <ReadyToTestLink />
          </FlexItem>
          <FlexItem>
            <NeedToTrainFirstLink />
          </FlexItem>
        </Flex>
      </StackItem>
    </Stack>
  );
}
